import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import { insightWorkbenchApi } from '../api/InsightWorkbenchApi'
import { ExecuteInsightFlow } from '../flows/ExecuteInsightFlow'
import type { ExecutePayloadInput, ExecuteResponse } from '../models/execute.types'
import type { InsightResult } from '../models/insight.types'
import type {
  InsightWorkbenchConfigParams,
  InsightWorkbenchResultParams,
} from '../models/insightApi.types'
import type { InsightConfig } from '../models/insightConfig.types'
import type {
  ExecuteInsightOutcome,
  InsightWorkbenchTab,
  WorkbenchAsyncState,
} from '../models/workbench.types'
import {
  summarizeWorkbenchOutcomes,
  type InsightSummaryCardModel,
} from '../service/insightWorkbench'

import { useInsightConfigBrowser, type UseInsightConfigBrowser } from './useInsightConfigBrowser'
import { useInsightExecute, type UseInsightExecute } from './useInsightExecute'
import { useInsightHistory, type UseInsightHistory } from './useInsightHistory'
import { useInsightPreview, type UseInsightPreview } from './useInsightPreview'
import { useInsightsRbac } from './useInsightsRbac'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError, type Paginated } from '@/shared/http'

interface InsightWorkbenchPort {
  listConfigs: (
    accountId: number,
    workspaceId: number,
    params?: InsightWorkbenchConfigParams,
    signal?: AbortSignal,
  ) => Promise<Paginated<InsightConfig>>
  getConfig: (
    accountId: number,
    workspaceId: number,
    configId: number,
    signal?: AbortSignal,
  ) => Promise<InsightConfig>
  listResults: (
    accountId: number,
    workspaceId: number,
    params?: InsightWorkbenchResultParams,
    signal?: AbortSignal,
  ) => Promise<Paginated<InsightResult>>
  getResult: (
    accountId: number,
    workspaceId: number,
    insightId: number,
    tab?: string,
    signal?: AbortSignal,
  ) => Promise<InsightResult>
  execute: (
    accountId: number,
    workspaceId: number,
    payload: ExecutePayloadInput,
    signal?: AbortSignal,
  ) => Promise<ExecuteResponse>
}

export interface UseInsightWorkbenchOptions {
  accountId?: MaybeRefOrGetter<number | undefined>
  workspaceId?: MaybeRefOrGetter<number | string | undefined>
  canUseWorkbench?: MaybeRefOrGetter<boolean>
  api?: InsightWorkbenchPort
}

export interface UseInsightWorkbench {
  accountId: ComputedRef<number | undefined>
  workspaceId: ComputedRef<number | undefined>
  hasContext: ComputedRef<boolean>
  canUseWorkbench: ComputedRef<boolean>
  state: ComputedRef<WorkbenchAsyncState>
  error: ComputedRef<ApiError | null>
  activeTab: Ref<InsightWorkbenchTab>
  selectedConfigId: Ref<number | null>
  selectedConfig: ComputedRef<InsightConfig | null>
  summaryCards: ComputedRef<readonly InsightSummaryCardModel[]>
  browser: UseInsightConfigBrowser
  history: UseInsightHistory
  execute: UseInsightExecute
  preview: UseInsightPreview
  selectConfig: (configId: number) => Promise<void>
  refreshAll: () => Promise<void>
  runDryRun: () => Promise<ExecuteInsightOutcome>
  persist: () => Promise<ExecuteInsightOutcome>
}

function numericId(value: string | number | undefined): number | undefined {
  if (value === undefined || (typeof value === 'string' && value.trim() === '')) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function useInsightWorkbench(options: UseInsightWorkbenchOptions = {}): UseInsightWorkbench {
  const account = options.accountId === undefined ? useAccount() : null
  const workspace = options.workspaceId === undefined ? useWorkspace() : null
  const rbac = options.canUseWorkbench === undefined ? useInsightsRbac() : null
  const api = options.api ?? insightWorkbenchApi
  const accountId = computed(() =>
    numericId(
      options.accountId === undefined ? account?.accountId.value : toValue(options.accountId),
    ),
  )
  const workspaceId = computed(() =>
    numericId(
      options.workspaceId === undefined
        ? workspace?.workspaceId.value
        : toValue(options.workspaceId),
    ),
  )
  const canUseWorkbench = computed(() =>
    options.canUseWorkbench === undefined
      ? (rbac?.canUseWorkbench.value ?? false)
      : toValue(options.canUseWorkbench),
  )
  const hasContext = computed(
    () => accountId.value !== undefined && workspaceId.value !== undefined,
  )
  const enabled = computed(() => hasContext.value && canUseWorkbench.value)
  const activeTab = ref<InsightWorkbenchTab>('preview')
  const selectedConfigId = ref<number | null>(null)
  const configDetail = ref<InsightConfig | null>(null)
  const configState = ref<WorkbenchAsyncState>('idle')
  const configError = ref<ApiError | null>(null)
  let configGeneration = 0
  let configController: AbortController | null = null

  const browser = useInsightConfigBrowser({
    accountId,
    workspaceId,
    enabled,
    api,
  })
  const selectedConfig = computed(
    () =>
      configDetail.value ??
      browser.configs.value.find((config) => config.id === selectedConfigId.value) ??
      null,
  )
  const history = useInsightHistory({
    accountId,
    workspaceId,
    configEnum: computed(() => selectedConfig.value?.enum),
    enabled,
    api,
  })
  const execute = useInsightExecute()
  const preview = useInsightPreview({
    accountId,
    workspaceId,
    currentSignature: execute.signature,
    flow: new ExecuteInsightFlow(api),
  })

  function resetConfig(): void {
    configGeneration += 1
    configController?.abort()
    configController = null
    selectedConfigId.value = null
    configDetail.value = null
    configState.value = 'idle'
    configError.value = null
    execute.reset()
    preview.reset()
    activeTab.value = 'preview'
  }

  async function selectConfig(configId: number): Promise<void> {
    if (!enabled.value || accountId.value === undefined || workspaceId.value === undefined) return
    const listConfig = browser.configs.value.find((config) => config.id === configId) ?? null
    selectedConfigId.value = configId
    configDetail.value = listConfig
    execute.setConfig(listConfig)
    preview.reset()
    activeTab.value = 'preview'

    const request = ++configGeneration
    configController?.abort()
    const activeController = new AbortController()
    configController = activeController
    const requestContext = `${accountId.value}:${workspaceId.value}`
    configState.value = 'loading'
    configError.value = null
    try {
      const detail = await api.getConfig(
        accountId.value,
        workspaceId.value,
        configId,
        activeController.signal,
      )
      if (
        request !== configGeneration ||
        activeController.signal.aborted ||
        requestContext !== `${accountId.value}:${workspaceId.value}` ||
        configId !== selectedConfigId.value
      ) {
        return
      }
      configDetail.value = detail
      execute.setConfig(detail)
      configState.value = 'ready'
    } catch (cause) {
      if (request !== configGeneration || activeController.signal.aborted) return
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind === 'canceled') return
      configError.value = normalized
      configState.value = 'error'
    }
  }

  async function refreshAll(): Promise<void> {
    await Promise.all([browser.load(), history.load()])
    if (selectedConfigId.value !== null) await selectConfig(selectedConfigId.value)
  }

  async function runDryRun(): Promise<ExecuteInsightOutcome> {
    if (!execute.validate()) {
      return { kind: 'invalid', errorKey: 'insights.sysadmin.execute.configRequired' }
    }
    activeTab.value = 'preview'
    const outcome = await preview.runDryRun(execute.payload(true))
    if (outcome.kind === 'preview_ready' || outcome.kind === 'skipped') execute.close()
    return outcome
  }

  async function persist(): Promise<ExecuteInsightOutcome> {
    if (!execute.validate()) {
      return { kind: 'invalid', errorKey: 'insights.sysadmin.execute.configRequired' }
    }
    const outcome = await preview.persist(execute.payload(false))
    if (outcome.kind === 'created') {
      execute.close()
      await history.load()
      const createdInsight = outcome.insight ?? history.results.value[0] ?? null
      if (createdInsight) await history.selectResult(createdInsight)
      activeTab.value = 'history'
    }
    return outcome
  }

  watch(
    () => browser.configs.value,
    (configs) => {
      if (!enabled.value || browser.state.value !== 'ready') return
      if (
        selectedConfigId.value === null ||
        !configs.some((config) => config.id === selectedConfigId.value)
      ) {
        const firstId = configs.find((config) => config.id !== undefined)?.id
        if (firstId !== undefined) void selectConfig(firstId)
      }
    },
  )
  watch(
    () => `${accountId.value ?? ''}:${workspaceId.value ?? ''}:${canUseWorkbench.value}`,
    resetConfig,
  )
  onScopeDispose(resetConfig)

  const state = computed<WorkbenchAsyncState>(() => {
    if (!hasContext.value || !canUseWorkbench.value) return 'idle'
    if (browser.state.value === 'error' || configState.value === 'error') return 'error'
    if (browser.state.value === 'loading' || configState.value === 'loading') return 'loading'
    if (browser.state.value === 'empty') return 'empty'
    return browser.state.value === 'ready' ? 'ready' : 'idle'
  })

  return {
    accountId,
    workspaceId,
    hasContext,
    canUseWorkbench,
    state,
    error: computed(() => configError.value ?? browser.error.value),
    activeTab,
    selectedConfigId,
    selectedConfig,
    summaryCards: computed(() => summarizeWorkbenchOutcomes(history.results.value)),
    browser,
    history,
    execute,
    preview,
    selectConfig,
    refreshAll,
    runDryRun,
    persist,
  }
}
